import * as React from "react";
import { CalendarCheck, Check, CheckCheck, MapPin, MoreHorizontal, X } from "lucide-react";
import {
  Button,
  Checkbox,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTableScroll,
  DataTableSelectionSummary,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { scrollToCenter } from "@/lib/teamPay/registerScroll";
import {
  ATTENDANCE_STATUS_LABELS,
  ATTENDANCE_STATUS_SHORT,
  WEEKDAY_SHORT,
  formatDay,
  istToday,
  monthLabel,
  parseDay,
  useAttendanceRegister,
  useBulkMarkAttendance,
  useDecideAttendance,
  useDeleteAttendance,
  useMarkAttendance,
  usePendingAttendance,
  type AttendanceStatus,
  type RegisterDay,
  type RegisterRow,
} from "@/lib/teamPay/api";
import { ErrorBanner } from "@/components/team-pay/shared";
import { errorMessage, useTeamWords } from "@/lib/teamPay/words";

/**
 * Phase SC-22d — the month register (one row per member, one cell per
 * day) and the queue of check-ins waiting for approval.
 */

const CELL_TONE: Record<AttendanceStatus, string> = {
  present: "bg-primary/15 text-primary",
  half_day: "bg-primary/10 text-primary",
  absent: "bg-destructive/10 text-destructive",
  leave: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  holiday: "bg-muted text-muted-foreground",
  week_off: "bg-muted/60 text-muted-foreground",
};

type MarkChoice =
  | { status: "present" | "half_day" | "absent" }
  | { status: "leave"; paid: boolean }
  | { status: "clear" };

const CHOICES: { label: string; choice: MarkChoice }[] = [
  { label: "Present", choice: { status: "present" } },
  { label: "Half day", choice: { status: "half_day" } },
  { label: "Absent", choice: { status: "absent" } },
  { label: "Paid leave", choice: { status: "leave", paid: true } },
  { label: "Unpaid leave", choice: { status: "leave", paid: false } },
];

function weekdayShort(iso: string): string {
  const [y, m, d] = parseDay(iso);
  const idx = (new Date(y, m - 1, d).getDay() + 6) % 7;
  return (WEEKDAY_SHORT[idx] ?? "").slice(0, 2);
}

function RegisterCell({
  row,
  day,
  today,
  canMark,
  onMark,
}: {
  row: RegisterRow;
  day: RegisterDay;
  today: string;
  canMark: boolean;
  onMark: (row: RegisterRow, day: RegisterDay, choice: MarkChoice) => void;
}) {
  const future = day.day > today;
  const pending = day.approval_status === "pending";
  const shown: AttendanceStatus | null =
    day.status ?? (day.holiday ? "holiday" : day.week_off ? "week_off" : null);
  const label = shown ? ATTENDANCE_STATUS_LABELS[shown] : "Not marked";
  const face = (
    <span
      className={cn(
        "grid size-7 place-items-center rounded-md text-[11px] font-semibold",
        shown ? CELL_TONE[shown] : "text-muted-foreground/50",
        pending && "ring-offset-background ring-1 ring-amber-400 ring-offset-1",
      )}
    >
      {shown ? ATTENDANCE_STATUS_SHORT[shown] : "·"}
    </span>
  );

  if (!canMark || future) {
    return (
      <span title={`${formatDay(day.day)} · ${label}${pending ? " · waiting" : ""}`}>{face}</span>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`${row.name}, ${formatDay(day.day)}: ${label}${pending ? ", waiting for approval" : ""}`}
          className="focus-visible:ring-ring rounded-md transition-transform duration-100 ease-out focus-visible:outline-none focus-visible:ring-1 active:scale-[0.97]"
        >
          {face}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-40">
        <DropdownMenuLabel className="text-xs">{formatDay(day.day)}</DropdownMenuLabel>
        {CHOICES.map(({ label: l, choice }) => (
          <DropdownMenuItem key={l} onSelect={() => onMark(row, day, choice)}>
            {l}
          </DropdownMenuItem>
        ))}
        {day.record_id ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onMark(row, day, { status: "clear" })}>
              Clear
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PendingQueue({ canApprove }: { canApprove: boolean }) {
  const words = useTeamWords();
  const pending = usePendingAttendance();
  const decide = useDecideAttendance();
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const items = React.useMemo(() => pending.data?.items ?? [], [pending.data]);

  React.useEffect(() => {
    setSelected((prev) => new Set([...prev].filter((id) => items.some((i) => i.id === id))));
  }, [items]);

  const allChecked = items.length > 0 && selected.size === items.length;
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const run = async (ids: string[], decision: "approve" | "reject") => {
    try {
      const res = await decide.mutateAsync({ ids, decision });
      toast.success(
        `${decision === "approve" ? "Approved" : "Rejected"} ${res.updated} day${res.updated === 1 ? "" : "s"}.`,
      );
      setSelected(new Set());
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't save the decision."));
    }
  };

  return (
    <section className="space-y-3" aria-labelledby="tp-pending-heading">
      <div>
        <h2 id="tp-pending-heading" className="text-base font-semibold tracking-tight">
          Waiting for approval
        </h2>
        <p className="text-muted-foreground text-sm">
          Check-ins that need a yes or no before they count towards pay.
        </p>
      </div>
      {pending.isError ? (
        <ErrorBanner
          message={errorMessage(pending.error, "Couldn't load check-ins.")}
          onRetry={() => pending.refetch()}
        />
      ) : null}
      <DataTable responsive="cards">
        {canApprove ? (
          <DataTableToolbar>
            <DataTableToolbarSpacer />
            <Button
              size="sm"
              variant="outline"
              disabled={selected.size === 0 || decide.isPending}
              onClick={() => run([...selected], "reject")}
            >
              <X aria-hidden="true" className="size-4" />
              Reject
            </Button>
            <Button
              size="sm"
              disabled={selected.size === 0 || decide.isPending}
              onClick={() => run([...selected], "approve")}
              data-testid="bulk-approve"
            >
              <CheckCheck aria-hidden="true" className="size-4" />
              Approve selected
            </Button>
          </DataTableToolbar>
        ) : null}
        <DataTableScroll className="max-h-[420px]">
          <Table>
            <TableCaption className="sr-only">Check-ins waiting for approval</TableCaption>
            <TableHeader>
              <TableRow>
                {canApprove ? (
                  <TableHead className="w-10">
                    <span className="sr-only">Select row</span>
                    <Checkbox
                      aria-label="Select all"
                      checked={allChecked}
                      onCheckedChange={(v) =>
                        setSelected(v ? new Set(items.map((i) => i.id)) : new Set())
                      }
                    />
                  </TableHead>
                ) : null}
                <TableHead>{words.member}</TableHead>
                <TableHead>Day</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Where</TableHead>
                {canApprove ? (
                  <TableHead className="w-[1%] text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                ) : null}
              </TableRow>
            </TableHeader>
            {pending.isLoading ? (
              <DataTableSkeleton rows={5} columns={canApprove ? 6 : 4} />
            ) : (
              <TableBody>
                {items.map((rec) => (
                  <TableRow
                    key={rec.id}
                    className="hover:bg-muted/40 transition-colors duration-[120ms]"
                  >
                    {canApprove ? (
                      <TableCell>
                        <Checkbox
                          aria-label={`Select ${rec.member_name ?? "check-in"} on ${formatDay(rec.day)}`}
                          checked={selected.has(rec.id)}
                          onCheckedChange={() => toggle(rec.id)}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell className="font-medium">{rec.member_name ?? "—"}</TableCell>
                    <TableCell title={rec.day}>{formatDay(rec.day)}</TableCell>
                    <TableCell>{ATTENDANCE_STATUS_LABELS[rec.status]}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {rec.inside_geofence === true ? (
                        <span className="inline-flex items-center gap-1">
                          <MapPin aria-hidden="true" className="size-3" /> At the centre
                        </span>
                      ) : rec.distance_m !== null ? (
                        `${Math.round(rec.distance_m)} m away`
                      ) : rec.source === "self_manual" ? (
                        "No location"
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    {canApprove ? (
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="size-8"
                              disabled={decide.isPending}
                              aria-label={`Decide ${rec.member_name ?? "check-in"} on ${formatDay(rec.day)}`}
                            >
                              <MoreHorizontal aria-hidden="true" className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem onSelect={() => run([rec.id], "approve")}>
                              <Check aria-hidden="true" className="size-4" />
                              Approve
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => run([rec.id], "reject")}>
                              <X aria-hidden="true" className="size-4" />
                              Reject
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>
        {!pending.isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<CheckCheck aria-hidden="true" />}
            title="All caught up"
            description="New check-ins that need approval will show here."
          />
        ) : null}
        {canApprove ? (
          <DataTableFooter>
            <DataTableSelectionSummary selected={selected.size} total={items.length} />
          </DataTableFooter>
        ) : null}
      </DataTable>
    </section>
  );
}

export function AttendanceTab({
  year,
  month,
  canMark,
  canApprove,
}: {
  year: number;
  month: number;
  canMark: boolean;
  canApprove: boolean;
}) {
  const words = useTeamWords();
  const register = useAttendanceRegister(year, month);
  const mark = useMarkAttendance();
  const bulk = useBulkMarkAttendance();
  const clear = useDeleteAttendance();
  const today = istToday();
  const rows = register.data?.rows ?? [];
  const days = rows[0]?.days ?? [];
  const showsToday = today.startsWith(`${year}-${String(month).padStart(2, "0")}`);
  const registerScrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const root = registerScrollRef.current;
    if (!root || !showsToday || register.isLoading) return;
    const target = root.querySelector<HTMLElement>('[data-register-today="true"]');
    if (!target) return;
    scrollToCenter(root, target);
  }, [showsToday, register.isLoading, register.data]);

  const onMark = async (row: RegisterRow, day: RegisterDay, choice: MarkChoice) => {
    try {
      if (choice.status === "clear") {
        if (day.record_id) await clear.mutateAsync(day.record_id);
        return;
      }
      await mark.mutateAsync({
        staff_payee_id: row.staff_payee_id,
        day: day.day,
        status: choice.status,
        leave_paid: choice.status === "leave" ? choice.paid : undefined,
      });
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't mark that day."));
    }
  };

  const markEveryonePresent = async () => {
    const entries = rows
      .filter((r) => {
        const d = r.days.find((x) => x.day === today);
        return d && !d.status && !d.week_off && !d.holiday;
      })
      .map((r) => ({ staff_payee_id: r.staff_payee_id, status: "present" as const }));
    if (entries.length === 0) {
      toast.success("Everyone is already marked for today.");
      return;
    }
    try {
      const res = await bulk.mutateAsync({ day: today, entries, overwrite: false });
      toast.success(`Marked ${res.marked} present for today.`);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't mark today."));
    }
  };

  return (
    <div className="space-y-8">
      <section className="space-y-3" aria-labelledby="tp-register-heading">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="tp-register-heading" className="text-base font-semibold tracking-tight">
              Register · {monthLabel(year, month)}
            </h2>
            <p className="text-muted-foreground text-sm">
              {canMark ? "Tap a day to mark it. " : ""}Days with an amber ring are waiting for
              approval.
            </p>
          </div>
          {canMark && showsToday && rows.length > 0 ? (
            <Button
              size="sm"
              variant="outline"
              disabled={bulk.isPending}
              onClick={markEveryonePresent}
            >
              <CalendarCheck aria-hidden="true" className="size-4" />
              Mark everyone present today
            </Button>
          ) : null}
        </div>

        {register.isError ? (
          <ErrorBanner
            message={errorMessage(register.error, "Couldn't load the register.")}
            onRetry={() => register.refetch()}
          />
        ) : null}

        <DataTable responsive="table">
          <DataTableScroll ref={registerScrollRef} className="max-h-[560px]">
            <Table className="w-max min-w-full">
              <TableCaption className="sr-only">
                Attendance register for {monthLabel(year, month)}
              </TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead className="bg-background sticky left-0 z-[2] min-w-40 shadow-[inset_-1px_0_0_hsl(var(--border))]">
                    {words.member}
                  </TableHead>
                  {days.map((d) => (
                    <TableHead
                      key={d.day}
                      data-register-today={d.day === today ? "true" : undefined}
                      className={cn(
                        "w-9 px-1 text-center text-[11px] font-medium",
                        d.day === today && "bg-primary/10 text-primary",
                      )}
                    >
                      <span className="block">{Number(d.day.slice(8))}</span>
                      <span className="text-muted-foreground block text-[10px] font-normal">
                        {weekdayShort(d.day)}
                      </span>
                    </TableHead>
                  ))}
                  <TableHead className="px-3 text-right text-xs">P</TableHead>
                  <TableHead className="px-3 text-right text-xs">A</TableHead>
                  <TableHead className="px-3 text-right text-xs">L</TableHead>
                </TableRow>
              </TableHeader>
              {register.isLoading ? (
                <DataTableSkeleton rows={5} columns={8} />
              ) : (
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.staff_payee_id}
                      className="hover:bg-muted/40 transition-colors duration-[120ms]"
                    >
                      <TableCell className="bg-background sticky left-0 z-[1] py-2 shadow-[inset_-1px_0_0_hsl(var(--border))]">
                        <span className="block text-sm font-medium">{row.name}</span>
                        <span className="text-muted-foreground block text-xs">
                          {row.role_label}
                          {row.pending ? ` · ${row.pending} waiting` : ""}
                        </span>
                      </TableCell>
                      {row.days.map((d) => (
                        <TableCell
                          key={d.day}
                          className={cn(
                            "px-1 py-1.5 text-center",
                            d.day === today && "bg-primary/10",
                          )}
                        >
                          <RegisterCell
                            row={row}
                            day={d}
                            today={today}
                            canMark={canMark}
                            onMark={onMark}
                          />
                        </TableCell>
                      ))}
                      <TableCell className="px-3 text-right tabular-nums">{row.present}</TableCell>
                      <TableCell className="px-3 text-right tabular-nums">{row.absent}</TableCell>
                      <TableCell className="px-3 text-right tabular-nums">{row.leave}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </DataTableScroll>
          {!register.isLoading && rows.length === 0 ? (
            <DataTableEmpty
              icon={<CalendarCheck aria-hidden="true" />}
              title={`No ${words.members.toLowerCase()} to mark`}
              description="Add people on the Pay tab first."
            />
          ) : null}
        </DataTable>
      </section>

      <PendingQueue canApprove={canApprove} />
    </div>
  );
}
